import {
  Base,
  Container,
  Label,
  Error,
  SetpointButton,
  IconWrapper,
  CustomInput,
  PlaceholderWrapper,
} from './styles';

import {
  ArrowUpIconV2,
  ArrowDownIconV2,
} from '../../icons';

import { toast } from 'react-toastify';
import { useStateVar } from 'helpers/useStateVar';
import {
  ChangeEvent, CSSProperties, KeyboardEvent, useEffect,
} from 'react';
import { t } from 'i18next';

export const InputCalculator = (cProps: {
  id?: string;
  label?: string;
  placeholder?: string;
  value: string;
  name?: string;
  disabled?: boolean;
  error?: string|null;
  unity?: string;
  suffix?: string;
  possibleValues?: number[]|null;
  lowerLimit?: number;
  isSetpoint?: boolean;
  allowManualInput?: boolean;
  invalidValueError?: string;
  limitMessage?: string;
  style?: CSSProperties;
  onIncreaseDecrease,
  onEnterKey?: () => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  type?: string;
}): JSX.Element => {
  const {
    disabled, label, value, name, error, unity, possibleValues, onEnterKey, placeholder, suffix, lowerLimit, isSetpoint, allowManualInput, invalidValueError, limitMessage, ...props
  } = cProps;

  const onKeyPress = (e) => {
    if (e.nativeEvent && e.nativeEvent.keyCode === 13 && onEnterKey) {
      e.preventDefault(); // Ensure it is only this code that runs
      onEnterKey();
    }
    if (cProps.lowerLimit != null && cProps.lowerLimit >= 0 && (e.code === 'Minus' || e.code === 'NumpadSubtract')) {
      e.preventDefault();
    }
  };

  function increaseDecrease(value: string) {
    props.onIncreaseDecrease(value);
  }

  return (
    <>
      {possibleValues ? (
        <SelectValue
          {...props}
          value={value}
          name={name}
          possibleValues={possibleValues}
          error={error}
          label={label}
          disabled={disabled}
          placeholder={placeholder}
          onKeyPress={onKeyPress}
          suffix={suffix}
          unity={unity}
          onIncreaseDecrease={increaseDecrease}
          isSetpoint={isSetpoint}
          allowManualInput={allowManualInput}
          invalidValueError={invalidValueError}
          limitMessage={limitMessage}
        />
      ) : (
        <InputValue
          {...props}
          value={value}
          name={name}
          error={error}
          label={label}
          disabled={disabled}
          placeholder={placeholder}
          lowerLimit={lowerLimit}
          unity={unity}
          onKeyPress={onKeyPress}
          suffix={suffix}
          onIncreaseDecrease={increaseDecrease}
        />
      )}
    </>
  );
};

function increaseDecreaseValue(value: string, increase: boolean, lowerLimit?: number) {
  let valueReturn = Number(value);
  if (increase) {
    valueReturn++;
  }
  else {
    valueReturn--;
    if (lowerLimit != null && valueReturn < lowerLimit) {
      valueReturn++;
      toast.warn(t('inputCalculatorNaoPermitidoUltrapassarLimiteInferior', { lowerLimit }));
    }
  }

  return `${valueReturn.toString()}`;
}

function increaseDecreaseValueList(
  value: string,
  possibleValues: number[],
  increase: boolean,
  unity?: string,
  isSetpoint?: boolean,
  limitMessage?: string,
) {
  if (!possibleValues?.length) return value;

  const valueNumber = Number(value);

  const safeValues = possibleValues
    .filter(Number.isFinite)
    .sort((a, b) => a - b);

  if (!safeValues.length) return value;

  let index = safeValues.indexOf(valueNumber);

  // 🔴 Se valor não existir na lista
  if (index === -1) {
    index = 0; // força para o primeiro válido
  }

  const nextIndex = increase
    ? Math.min(index + 1, safeValues.length - 1)
    : Math.max(index - 1, 0);

  const newValue = safeValues[nextIndex];

  if (newValue === undefined) {
    console.error('Valor indefinido detectado', {
      index,
      nextIndex,
      safeValues,
    });
    return value;
  }

  if (newValue === valueNumber) {
    if (limitMessage) {
      toast.info(limitMessage);
    } else {
      const key = increase ? 'inputCalculatorNaoPossivelIncrementar' : 'inputCalculatorNaoPossivelDecrementar';
      const type = isSetpoint ? t('setpoint') : t('inputCalculatorTipoValor');
      const suffix = isSetpoint ? t('inputCalculatorNecessarioCadastroComandoIr') : '';
      toast.info(t(key, { type, value: newValue, unity: unity ?? '' }) + suffix);
    }
  }

  return String(newValue);
}

const InputValue = (cProps: {
  value: string
  disabled?: boolean
  label?: string
  name?: string
  error?: string|null
  placeholder?: string
  unity?: string
  suffix?: string
  lowerLimit?: number
  onIncreaseDecrease,
  onKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void,
}): JSX.Element => {
  const [state, render, setState] = useStateVar({
    value: `${cProps.value != null ? cProps.value : '-'}`,
  });

  useEffect(() => {
    const newValue = cProps.value != null ? cProps.value : '-';
    if (newValue !== state.value) {
      setState({ value: newValue });
    }
  }, [cProps.value]);

  const {
    disabled, label, name, error, placeholder, suffix, unity, value, lowerLimit, ...props
  } = cProps;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (/^\d*\.?\d{0,1}$/.test(value)) {
      state.value = value;
      render();
    }
  };

  return (
    <CustomInput {...props}>
      <div style={{ width: '100%' }}>
        <Container>
          <Label disabled={disabled} htmlFor={name} value={state.value} error={error} suffixLength={suffix?.length}>
            <span>{label}</span>
            <Base
              style={{ border: '0px' }}
              value={state.value}
              name={name}
              error={error}
              disabled={disabled}
              placeholder={placeholder}
              suffixLength={suffix?.length}
              onChange={handleInputChange}
            />
            {suffix && (
              <span>{suffix}</span>
            )}
          </Label>
          {error && <Error>{error}</Error>}
        </Container>
      </div>
      {cProps.unity && (
        <PlaceholderWrapper>
          {cProps.unity}
        </PlaceholderWrapper>
      )}
      <div style={{
        width: '57px',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid #C5C5C5',
      }}
      >
        <SetpointButton disabled={disabled} up onClick={() => { setState({ value: increaseDecreaseValue(state.value, true, cProps.lowerLimit) }); props.onIncreaseDecrease(state.value); render(); }}>
          <IconWrapper>
            <ArrowUpIconV2 />
          </IconWrapper>
        </SetpointButton>
        <SetpointButton disabled={disabled} down onClick={() => { setState({ value: increaseDecreaseValue(state.value, false, cProps.lowerLimit) }); props.onIncreaseDecrease(state.value); render(); }}>
          <IconWrapper>
            <ArrowDownIconV2 />
          </IconWrapper>
        </SetpointButton>
      </div>
    </CustomInput>
  );
};

const SelectValue = (cProps: {
  value: string
  possibleValues: number[]
  disabled?: boolean
  label?: string
  name?: string
  error?: string|null
  unity?: string;
  placeholder?: string
  suffix?: string
  isSetpoint?: boolean
  allowManualInput?: boolean
  invalidValueError?: string
  limitMessage?: string
  style?: CSSProperties
  onIncreaseDecrease,
  onKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void,
}): JSX.Element => {
  const [state, render, setState] = useStateVar({
    value: `${cProps.value != null ? cProps.value : '-'}`,
    internalError: null as string | null,
  });

  useEffect(() => {
    const newValue = cProps.value != null ? cProps.value : '-';
    if (newValue !== state.value) {
      setState({ value: newValue, internalError: null });
    }
  }, [cProps.value]);

  const {
    disabled, label, name, error, placeholder, suffix, possibleValues, isSetpoint, allowManualInput, invalidValueError, limitMessage, onKeyPress, style, ...props
  } = cProps;

  const displayError = state.internalError ?? error;

  const safeStyle = style?.height
    ? { ...style, minHeight: style.height, height: undefined }
    : style;

  const handleManualChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const allIntegers = possibleValues.every((v) => Number.isInteger(v));
    const raw = allIntegers
      ? e.target.value.replace(/[^0-9]/g, '')
      : e.target.value;
    const num = Number(raw);
    const isValid = raw === '' || possibleValues.some((v) => v === num);
    const displayedValues = possibleValues.slice(0, 16);
    const valuesLabel = `${displayedValues.join(', ')}${possibleValues.length > 16 ? '...' : ''}`;
    const errorMsg = invalidValueError ?? `${t('inputCalculatorValorNaoPermitido')}. ${t('inputCalculatorValoresAceitos')}: ${valuesLabel}`;
    setState({
      value: raw,
      internalError: raw !== '' && !isValid ? errorMsg : null,
    });
    props.onIncreaseDecrease(raw);
    render();
  };

  return (
    <div>
      <CustomInput error={displayError} style={safeStyle} {...props}>
        <div style={{ width: '100%' }}>
          <Container>
            <Label disabled={disabled} htmlFor={name} value={state.value} error={displayError} suffixLength={suffix?.length}>
              <span>{label}</span>
              <Base
                style={{ border: '0px' }}
                value={state.value}
                name={name}
                error={displayError}
                disabled={allowManualInput ? disabled : true}
                placeholder={placeholder}
                suffixLength={suffix?.length}
                onKeyPress={onKeyPress}
                {...(allowManualInput ? { onChange: handleManualChange } : {})}
              />
              {suffix && (
                <span>{suffix}</span>
              )}
            </Label>
          </Container>
        </div>
        {cProps.unity && (
          <PlaceholderWrapper>
            {cProps.unity}
          </PlaceholderWrapper>
        )}
        <div style={{
          width: '57px',
          display: 'flex',
          flexDirection: 'column',
          borderLeft: '1px solid #C5C5C5',
        }}
        >
          <SetpointButton disabled={disabled} up onClick={() => { if (cProps.value != null) { setState({ value: increaseDecreaseValueList(state.value, possibleValues, true, cProps.unity, cProps.isSetpoint, limitMessage), internalError: null }); props.onIncreaseDecrease(state.value); render(); } }}>
            <IconWrapper>
              <ArrowUpIconV2 />
            </IconWrapper>
          </SetpointButton>
          <SetpointButton disabled={disabled} down onClick={() => { if (cProps.value != null) { setState({ value: increaseDecreaseValueList(state.value, possibleValues, false, cProps.unity, cProps.isSetpoint, limitMessage), internalError: null }); props.onIncreaseDecrease(state.value); render(); } }}>
            <IconWrapper>
              <ArrowDownIconV2 />
            </IconWrapper>
          </SetpointButton>
        </div>
      </CustomInput>
      {displayError && <Error>{displayError}</Error>}
    </div>
  );
};
